<template>
  <!-- Card 86akj8ptp (retorno de 26/09): box de notificações na página
       inicial, no mesmo desenho do de homework. Só o título; clicar abre a
       notificação na página de notificações. As fixadas vêm primeiro. -->
  <div class="np-card">
    <div class="np-title">
      <v-icon size="20" class="mr-2" :color="accent">mdi-bell-outline</v-icon>
      {{ $t("notifications.title") }}
      <span v-if="unread" class="np-unread">
        {{ $tc("notifications.newCount", unread, { count: unread }) }}
      </span>
    </div>

    <page-skeleton v-if="loading" variant="rows" :rows="3" />

    <div v-else-if="failed" class="np-note">
      {{ $t("notifications.loadFailed") }}
    </div>

    <div v-else-if="items.length" class="np-list">
      <button
        v-for="item in items"
        :key="item.id"
        type="button"
        class="np-item"
        :class="{ 'np-item--unread': !item.read }"
        @click="open(item)"
      >
        <span
          v-if="!item.read"
          class="np-dot"
          :style="{ background: accent }"
        />
        <span
          v-if="isPinned(item)"
          class="np-pin"
          :title="$t('notifications.pinnedTitle')"
          >📌</span
        >
        <span class="np-item__title">{{ item.title }}</span>
      </button>
    </div>

    <div v-else class="np-note">{{ $t("notifications.allDone") }}</div>

    <div class="np-footer">
      <v-btn
        text
        x-small
        class="np-link"
        :style="{ color: accent }"
        @click="openAll"
      >
        {{ $t("notifications.seeAll") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import pitchCustomerApi from "@/shared/pitchCustomerApi";
import pitchPartnerApi from "@/shared/pitchPartnerApi";

const LIMIT = 5;

export default {
  name: "NotificationsPreview",

  props: {
    // Quem está vendo: a caixa de entrada é a mesma, a API não.
    profile: {
      type: String,
      default: "customer",
      validator: (value) => ["customer", "partner"].includes(value),
    },
    accent: { type: String, default: "#A826E1" },
  },

  data() {
    return {
      notifications: [],
      unread: 0,
      loading: false,
      failed: false,
    };
  },

  computed: {
    base() {
      return this.profile === "partner"
        ? "/connect/professor/notificacoes"
        : "/connect/aluno/notificacoes";
    },
    // A API já põe as fixadas no topo; a ordenação aqui só garante isso
    // mesmo se a lista vier de outra origem. `sort` estável mantém o resto
    // na ordem de chegada (mais recentes primeiro).
    // Retorno de 27/10 (card 17trt6jfe9k): só o que pede atenção -- fixadas,
    // não lidas e votações sem resposta. As lidas ficam na caixa de entrada.
    items() {
      return [...this.notifications]
        .filter((item) => this.needsAttention(item))
        .sort((a, b) => Number(this.isPinned(b)) - Number(this.isPinned(a)))
        .slice(0, LIMIT);
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    needsAttention(item) {
      return (
        this.isPinned(item) ||
        !item.read ||
        (!!item.voting && item.voteAnswered === false)
      );
    },

    isPinned(item) {
      return !!item?.communication?.pinned;
    },

    async load() {
      this.loading = true;
      this.failed = false;

      try {
        if (this.profile === "partner") {
          const res = await pitchPartnerApi.getNotifications(1);
          this.notifications = res.data?.notifications || [];
          this.unread = Number(res.data?.unread) || 0;
        } else {
          const res = await pitchCustomerApi.getNotifications();
          this.notifications = res.data?.data || [];
          this.unread = Number(res.data?.unread) || 0;
        }
      } catch {
        // Uma falha aqui não pode derrubar a página inicial.
        this.notifications = [];
        this.failed = true;
      } finally {
        this.loading = false;
      }
    },

    open(item) {
      this.$router
        .push({ path: this.base, query: { id: item.id } })
        .catch(() => null);
    },

    openAll() {
      this.$router.push(this.base).catch(() => null);
    },
  },
};
</script>

<style scoped>
.np-card {
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line, var(--dk-line, #ececef));
  border-radius: 20px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
  padding: 22px;
  /* Card 86aknf6a0: quando a página inicial estica o box até a altura da
     linha, o "Ver todas" continua no rodapé. */
  display: flex;
  flex-direction: column;
}

.np-title {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--ink, var(--dk-ink, #231a2e));
}

.np-unread {
  margin-left: auto;
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--muted, var(--dk-muted, #7c7c7f));
}

.np-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.np-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 14px;
  border: 1px solid var(--line, var(--dk-line, #ececef));
  background: var(--paper, #faf9fb);
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease;
}

.np-item:hover,
.np-item:focus-visible {
  border-color: currentColor;
  outline: none;
}

.np-item__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.88rem;
  color: var(--ink, var(--dk-ink, #231a2e));
}

.np-item--unread .np-item__title {
  font-weight: 700;
}

.np-dot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.np-pin {
  flex: none;
  font-size: 0.85rem;
}

.np-note {
  font-size: 0.85rem;
  color: var(--muted, var(--dk-muted, #7c7c7f));
  line-height: 1.5;
}

.np-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 8px;
}

.np-link.v-btn {
  text-transform: none;
  letter-spacing: 0;
}
</style>
